import { useState } from "react";
import { HeartHandshake, Plus, Check, Trash2, Sparkle } from "lucide-react";
import PageShell from "../components/PageShell";
import { getPrayers, addPrayer, updatePrayer, deletePrayer } from "../lib/personal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const CATEGORIES = ["General", "Family", "Health", "Guidance", "Gratitude", "Others"];

export default function PrayerJournal() {
  const [items, setItems] = useState(getPrayers());
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [filter, setFilter] = useState("active");

  const refresh = () => setItems(getPrayers());

  const save = () => {
    if (!title.trim()) return;
    addPrayer({ title: title.trim(), content: content.trim(), category });
    setTitle(""); setContent(""); setCategory("General"); setOpen(false);
    refresh();
  };

  const shown = items.filter((p) =>
    filter === "answered" ? p.answered : filter === "active" ? !p.answered : true
  );
  const answeredCount = items.filter((p) => p.answered).length;

  return (
    <PageShell
      title="Prayer Journal"
      subtitle={`${items.length} prayers · ${answeredCount} answered`}
      icon={HeartHandshake}
      accent={{ a: "#C0563B", b: "#8A3F66", c: "#2C2A55" }}
      testid="journal-page"
    >
      <div className="mb-6 flex items-center gap-2">
        {["active", "answered", "all"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`font-ui rounded-lg px-3 py-1.5 text-sm capitalize transition-colors ${
              filter === f ? "bg-muted font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {f}
          </button>
        ))}
        <button
          onClick={() => setOpen(true)}
          data-testid="add-prayer"
          className="font-ui ml-auto flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105"
        >
          <Plus className="h-4 w-4" /> New Prayer
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-background/30 p-12 text-center">
          <HeartHandshake className="mx-auto mb-3 h-8 w-8 text-primary/60" />
          <p className="font-ui text-sm text-muted-foreground">
            No prayers here yet. Tap “New Prayer” to begin.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((p) => (
            <div
              key={p.id}
              data-testid={`prayer-${p.id}`}
              className={`rounded-2xl border p-5 backdrop-blur-md transition-colors ${
                p.answered ? "border-primary/40 bg-primary/5" : "border-border bg-background/40"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="font-ui inline-block rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    {p.category}
                  </span>
                  <h3 className="font-serif-display mt-2 text-xl">{p.title}</h3>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { updatePrayer(p.id, { answered: !p.answered }); refresh(); }}
                    data-testid={`answer-prayer-${p.id}`}
                    title="Mark answered"
                    className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                      p.answered ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { deletePrayer(p.id); refresh(); }}
                    data-testid={`delete-prayer-${p.id}`}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {p.content && (
                <p className="font-ui mt-2 text-sm leading-relaxed text-foreground/80">{p.content}</p>
              )}
              {p.answered && (
                <p className="font-ui mt-3 flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <Sparkle className="h-3.5 w-3.5" /> Answered — give thanks!
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="font-ui" data-testid="prayer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">New Prayer</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="prayer-title"
            placeholder="What are you praying for?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            data-testid="prayer-content"
            placeholder="Add details (optional)…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
          />
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  category === c ? "border-primary bg-primary text-primary-foreground" : "border-border"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <button
            onClick={save}
            data-testid="save-prayer"
            className="mt-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            Save Prayer
          </button>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
